import React, { HTMLAttributes } from 'react';
import ReactMarkdown from 'react-markdown';

import clsx from 'clsx';

import { TextAlignment } from '../../types';

import type { BaseCfComponent } from '../../types';

export enum TitleSize {
  H1 = 'h1',
  H1XL = 'h1xl',
  H2 = 'h2',
  H3 = 'h3',
  H4 = 'h4',
  P1 = 'p1',
  P2 = 'p2'
}

export enum BodySize {
  P1 = 'p1',
  P2 = 'p2'
}

export enum HeadingStyle {
  EditorialH1 = 'Editorial - H1',
  EditorialH1XL = 'Editorial - H1XL',
  EditorialH2 = 'Editorial - H2',
  EditorialH3 = 'Editorial - H3',
  EditorialH4 = 'Editorial - H4',
  EditorialP1 = 'Editorial - P1',
  H1 = 'H1',
  H1XL = 'H1XL',
  H2 = 'H2',
  H3 = 'H3',
  H4 = 'H4',
  P1 = 'P1',
  P1P2 = 'P1-P2',
  P2P1 = 'P2-P1'
}

export enum TitleTag {
  H1 = 'h1',
  H2 = 'h2',
  H3 = 'h3'
}

export interface TextCombinationProps extends BaseCfComponent {
  fields: {
    body?: string;
    emphasisTitle?: string;
    eyebrow?: string;
    headingStyle?: HeadingStyle
    textAlignment?: TextAlignment;
    title: React.ReactNode;
  },
  className?: string;
  titleTag?: TitleTag;
}

const getTitleComponent = (headingStyle?: HeadingStyle) => {
  switch (headingStyle) {
    case HeadingStyle.H1XL:
    case HeadingStyle.H1:
    case HeadingStyle.H2:
    case HeadingStyle.H3: {
      return 'h2';
    }
    default: {
      return 'span';
    }
  }
};

const getTitleBodyStyle = (headingStyle?: HeadingStyle) => {
  let titleFontColor = 'text-text-primary';
  let titleFontSize = 'text-h1';
  let titleFontWeight = 'font-bold';
  let bodyFontColor = 'text-text-secondary';
  let bodyFontSize = 'text-p1';
  let bodyFontWeight = '';

  switch (headingStyle) {
    case HeadingStyle.EditorialH1XL:
    case HeadingStyle.H1XL: {
      titleFontSize = 'text-h1xl';
      break;
    }
    case HeadingStyle.EditorialH2:
    case HeadingStyle.H2: {
      titleFontSize = 'text-h2';
      break;
    }
    case HeadingStyle.EditorialH3:
    case HeadingStyle.H3: {
      titleFontSize = 'text-h3';
      break;
    }
    case HeadingStyle.EditorialH4:
    case HeadingStyle.H4: {
      titleFontSize = 'text-h4';
      break;
    }
    case HeadingStyle.EditorialP1: {
      titleFontSize = 'text-h5';
      break;
    }
    case HeadingStyle.P1: {
      titleFontSize = 'text-p1';
      titleFontWeight = 'font-medium';

      break;
    }
    case HeadingStyle.P2P1: {
      titleFontColor = 'text-text-secondary';
      titleFontSize = 'text-p2';
      titleFontWeight = 'font-medium';
      bodyFontColor = 'text-text-primary';
      bodyFontSize = 'text-p1';
      bodyFontWeight = 'font-medium';
      break;
    }
    case HeadingStyle.P1P2: {
      titleFontSize = 'text-p1';
      bodyFontSize = 'text-p2';
    }
  }

  return {
    bodyClassName: clsx(bodyFontColor, bodyFontSize, bodyFontWeight, 'flex flex-col gap-sm'),
    titleClassName: clsx(titleFontColor, titleFontSize, titleFontWeight),
  };
};

const MarkdownLinkStyles = (props: HTMLAttributes<HTMLAnchorElement>) => (
  <a
    className="hover:text-text-hyperlink text-text-primary underline underline-offset-4 duration-300 ease-in-out"
    {...props}
  />
);

const getTitleBodyGap = (headingStyle?: HeadingStyle) => {
  switch (headingStyle) {
    case HeadingStyle.EditorialH1:
    case HeadingStyle.EditorialH1XL: {
      return 'gap-md';
    }
    case HeadingStyle.EditorialH3: {
      return 'gap-xs';
    }
    case HeadingStyle.EditorialH4:
    case HeadingStyle.H3:
    case HeadingStyle.H4:
    case HeadingStyle.P1P2: {
      return 'gap-2xs';
    }
    case HeadingStyle.EditorialP1:
    case HeadingStyle.P1: {
      return 'gap-3xs';
    }
    case HeadingStyle.P2P1: {
      return 'gap-0';
    }

    default: {
      return 'gap-sm';
    }
  }
};

const TextCombination = ({
  className,
  fields: {
    body,
    emphasisTitle,
    eyebrow,
    headingStyle,
    textAlignment,
    title,
  },
  titleTag,
}: TextCombinationProps) => {
  const TitleComponent = titleTag ?? getTitleComponent(headingStyle);
  const { bodyClassName, titleClassName } = getTitleBodyStyle(headingStyle);

  return (
    <div
      className={clsx(
        'flex flex-col w-full max-w-lg',
        textAlignment === TextAlignment.Center && 'text-center items-center mx-auto',
        className
      )}
    >
      {!!eyebrow && <span className="pb-xs text-overline text-text-primary uppercase font-medium">{eyebrow}</span>}
      <div className={clsx('flex flex-col', getTitleBodyGap(headingStyle))}>
        <TitleComponent className={titleClassName}>
          {title}{' '}
          {!!emphasisTitle && <span className="text-text-highlight">{emphasisTitle}</span>}
        </TitleComponent>
        {!!body && (
          <span className={bodyClassName}>
            <ReactMarkdown components={{ a: MarkdownLinkStyles }}>
              {body}
            </ReactMarkdown>
          </span>
        )}
      </div>
    </div>
  );
};

export default TextCombination;
